import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { AuditoriaUsuario, AuditoriaUsuariosParams } from "../../../services/auditoriaUsuarios";
import { formatarDataBrasilia } from "../../../utils/formatDate";
import useAuditoriaUsuarios from "./useAuditoriaUsuarios";
import DetalhesUsuario, { permissoesLabels } from "./DetalhesUsuario";

const filtros = [
  { value: "nome", label: "Nome" },
  { value: "email", label: "E-mail" },
  { value: "id_usuarios", label: "ID" },
  { value: "projeto", label: "Projeto" },
  { value: "permissao", label: "Permissão" },
  { value: "tipo", label: "Tipo de usuário" },
];
const colunas = [
  { key: "id_usuarios", label: "ID", sort: true },
  { key: "nome", label: "Nome", sort: true },
  { key: "email", label: "E-mail", sort: true },
  { key: "ativo", label: "Situação", sort: true },
  { key: "administrador", label: "Perfil", sort: true },
  { key: "tipo", label: "Tipo", sort: true },
  { key: "projetos", label: "Projetos", sort: false },
  { key: "ultimo_acesso", label: "Último acesso registrado", sort: false },
];

export default function TableComponent() {
  const audit = useAuditoriaUsuarios();
  const { params, query, resumo } = audit;
  const rows = query.data?.data ?? [];
  const [usuario, setUsuario] = useState<AuditoriaUsuario | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  async function exportar(format: "csv" | "xlsx") {
    setIsExporting(true);
    try {
      const data = await audit.exportar();
      if (!data.length) {
        toast("Nenhum usuário encontrado para os filtros aplicados.");
        return;
      }
      const formatted = data.map((row) => ({
        ID: row.id_usuarios,
        Nome: row.nome || "",
        "E-mail": row.email,
        Situação: row.ativo ? "Ativo" : "Inativo",
        Perfil: row.administrador ? "Administrador da organização" : "Por projeto e camada",
        Tipo: row.tipo || "",
        Projetos: row.projetos,
        "Último acesso registrado": row.ultimo_acesso ? formatarDataBrasilia(row.ultimo_acesso) : "Sem registro",
        "IP do último acesso": row.ultimo_ip || "",
      }));
      const sheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Usuários");
      if (format === "csv") {
        const safeRows = formatted.map((row) =>
          Object.fromEntries(
            Object.entries(row).map(([key, value]) => [
              key,
              typeof value === "string" && /^[=+@\-\t\r]/.test(value) ? `'${value}` : value,
            ]),
          ),
        );
        const csv = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(safeRows), { FS: ";" });
        const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "auditoria_usuarios.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } else {
        XLSX.writeFile(workbook, "auditoria_usuarios.xlsx", { bookType: "xlsx" });
      }
      toast.success("Consulta exportada com sucesso.");
    } catch {
      toast.error("Não foi possível exportar os usuários. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  }

  function value(row: AuditoriaUsuario, key: string) {
    if (key === "ativo")
      return <Chip size="small" label={row.ativo ? "Ativo" : "Inativo"} color={row.ativo ? "success" : "default"} />;
    if (key === "administrador") return row.administrador ? "Administrador da organização" : "Por projeto e camada";
    if (key === "ultimo_acesso") return row.ultimo_acesso ? formatarDataBrasilia(row.ultimo_acesso) : "Sem registro";
    if (key === "projetos")
      return (
        <Button size="small" onClick={() => setUsuario(row)} aria-label={`Ver projetos de ${row.nome || row.email}`}>
          {row.projetos ?? 0}
        </Button>
      );
    return row[key] ?? "—";
  }

  return (
    <Box sx={{ width: "90%", margin: "auto", pb: 4 }}>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Auditar usuários
          </Typography>
          <Typography color="text.secondary">
            Consulte contas, permissões em projetos e camadas e acessos registrados.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" color="inherit" onClick={() => audit.refresh()} disabled={query.isFetching}>
            Atualizar
          </Button>
          <Button variant="outlined" color="inherit" onClick={() => exportar("csv")} disabled={isExporting}>
            Exportar CSV
          </Button>
          <Button variant="outlined" color="inherit" onClick={() => exportar("xlsx")} disabled={isExporting}>
            {isExporting ? "Exportando…" : "Exportar XLSX"}
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
        {[
          { key: "totalRecords", label: "Usuários encontrados" },
          { key: "ativos", label: "Ativos" },
          { key: "inativos", label: "Inativos" },
          { key: "administradores", label: "Administradores da organização" },
        ].map((item) => (
          <Box key={item.key} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="h5">{resumo.data?.[item.key] ?? "—"}</Typography>
          </Box>
        ))}
      </Box>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          audit.apply();
        }}
        sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center", flexWrap: "wrap" }}
      >
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="usuarios-coluna-label">Coluna</InputLabel>
          <Select
            labelId="usuarios-coluna-label"
            label="Coluna"
            value={audit.coluna}
            onChange={(e) => {
              audit.setColuna(e.target.value);
              audit.setFiltro("");
            }}
          >
            {filtros.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {audit.coluna === "permissao" ? (
          <FormControl sx={{ minWidth: 260, flex: 1 }}>
            <InputLabel id="usuarios-permissao-label">Permissão</InputLabel>
            <Select
              labelId="usuarios-permissao-label"
              label="Permissão"
              value={audit.filtro}
              onChange={(e) => audit.setFiltro(e.target.value)}
            >
              {Object.entries(permissoesLabels)
                .filter(([key]) => key !== "POR_CAMADA")
                .map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Filtro"
            value={audit.filtro}
            onChange={(e) => audit.setFiltro(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            inputProps={{ maxLength: 500 }}
          />
        )}
        <Button type="submit" color="success" variant="contained" disabled={!audit.valid} sx={{ height: 56 }}>
          Filtrar
        </Button>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="usuarios-status-label">Situação</InputLabel>
          <Select
            labelId="usuarios-status-label"
            label="Situação"
            value={params.status}
            onChange={(e) => audit.update({ status: e.target.value as AuditoriaUsuariosParams["status"], offset: 0 })}
          >
            <MenuItem value="todos">Todas</MenuItem>
            <MenuItem value="ativos">Ativos</MenuItem>
            <MenuItem value="inativos">Inativos</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="usuarios-perfil-label">Perfil</InputLabel>
          <Select
            labelId="usuarios-perfil-label"
            label="Perfil"
            value={params.perfil}
            onChange={(e) => audit.update({ perfil: e.target.value as AuditoriaUsuariosParams["perfil"], offset: 0 })}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="administradores">Administradores da organização</MenuItem>
            <MenuItem value="demais">Por projeto e camada</MenuItem>
          </Select>
        </FormControl>
        <Button color="warning" variant="outlined" onClick={audit.clear} sx={{ height: 56 }}>
          Limpar filtros
        </Button>
      </Box>
      {resumo.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Não foi possível carregar os totais. A listagem continua disponível.
        </Alert>
      )}
      {query.error && (
        <Alert severity="error" action={<Button onClick={() => query.refetch()}>Tentar novamente</Button>}>
          Não foi possível carregar os usuários.
        </Alert>
      )}
      {query.isLoading && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <CircularProgress aria-label="Carregando usuários" />
        </Box>
      )}
      <TableContainer>
        <Table sx={{ minWidth: 1050 }} aria-label="Auditoria de usuários">
          <TableHead>
            <TableRow>
              {colunas.map((col) => (
                <TableCell key={col.key} sortDirection={params.orderBy === col.key ? params.direction : false}>
                  {col.sort ? (
                    <TableSortLabel
                      active={params.orderBy === col.key}
                      direction={params.orderBy === col.key ? params.direction : "asc"}
                      onClick={() => audit.sort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id_usuarios} hover>
                {colunas.map((col) => (
                  <TableCell key={col.key}>{value(row, col.key)}</TableCell>
                ))}
                <TableCell>
                  <Button onClick={() => setUsuario(row)} aria-label={`Auditar ${row.nome || row.email}`}>
                    Auditar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!query.isLoading && !query.error && !rows.length && (
              <TableRow>
                <TableCell colSpan={colunas.length + 1} align="center">
                  Nenhum usuário encontrado para os filtros aplicados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={resumo.data?.totalRecords ?? -1}
        page={params.offset / params.limit}
        rowsPerPage={params.limit}
        onPageChange={(_, page) => audit.update({ offset: page * params.limit })}
        onRowsPerPageChange={(e) => audit.update({ limit: Number(e.target.value), offset: 0 })}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        labelRowsPerPage="Linhas por página"
        nextIconButtonProps={{ disabled: query.isFetching || !query.data?.hasMore }}
      />
      {usuario && (
        <DetalhesUsuario
          key={`${audit.scope.join(":")}:${usuario.id_usuarios}`}
          usuario={usuario}
          scope={audit.scope}
          onClose={() => setUsuario(null)}
        />
      )}
    </Box>
  );
}
